import { v4 as uuidv4 } from "uuid";

import {
  PiBell,
  PiChatCircle,
  PiClipboardText,
  PiEnvelope,
  PiGearFine,
  PiLink,
  PiTextAlignLeftLight,
  PiTreeView,
} from "react-icons/pi";
import { LuPower, LuReply } from "react-icons/lu";
import { Edge, MarkerType, Node } from "@xyflow/react";
import { CiImageOn } from "react-icons/ci";

import { MessageNodeMediaSchema } from "../schemas/nodes/message-node/fields/media-field.schema";
import { MessageNodeQuickReplySchema } from "../schemas/nodes/message-node/fields/quick-reply-field.schema";
import { MessageNodeButtonSchema } from "../schemas/nodes/message-node/button-field.schema";
import { MessageNodeTextSchema } from "../schemas/nodes/message-node/fields/text-field.schema";

import { MessageNodeFieldTypeEnum, NodeTypeEnum } from "../enum/nodes.enum";
import {
  CustomConditions,
  PredefinedConditions,
} from "../schemas/nodes/branch-node";
import {
  FLOW_DEFAULT_X,
  FLOW_DEFAULT_Y,
  FLOW_HORIZONTAL_SPACING,
  FLOW_VERTICAL_SPACING,
} from "../constants/flow.constants";
import {
  constructVariableString,
  VariableInput,
} from "../store/variableInput.store";

function getAllNamesBetweenMustaches(str: string): string[] {
  const matches: string[] = [];
  const regex = /\{\{([^}]+)\}\}/g; // Match content inside mustache-style double curly braces

  let match: RegExpExecArray | null;
  while ((match = regex.exec(str)) !== null) {
    matches.push(match[1]!.trim()); // Extract and trim the content inside the mustaches
  }

  return matches;
}

export function getNodeDataForNodeType(args: {
  type: NodeTypeEnum;
  label: string;
  description: string;
  position: { x: number; y: number };
}) {
  const id = uuidv4();
  return {
    id,
    type: args.type,
    position: args.position,
    data: {
      id: id,
      label: args.label,
      description: args.description,
    },
  };
}

export function getEdgeBetweenNodes(args: {
  source: string;
  target: string;
  markerEnd?: {
    type: MarkerType;
  };
}) {
  return {
    id: `xy-edge__${args.source}-${args.target}`,
    source: args.source,
    target: args.target,
    markerEnd: args.markerEnd,
    type: "simplebezier",
  };
}

export function getIconByNodeType(type: NodeTypeEnum) {
  switch (type) {
    case NodeTypeEnum.START_NODE:
      return LuPower;
    case NodeTypeEnum.TRIGGER_NODE:
      return PiChatCircle;
    case NodeTypeEnum.COLLECT_NODE:
    case NodeTypeEnum.COLLECT_FORM_NODE:
      return PiClipboardText;
    case NodeTypeEnum.BRANCH_NODE:
      return PiTreeView;
    case NodeTypeEnum.MESSAGE_NODE:
      return PiChatCircle;
    case NodeTypeEnum.HTTP_ACTION_NODE:
      return PiGearFine;
    case NodeTypeEnum.EMAIL_ACTION_NODE:
      return PiEnvelope;
    case NodeTypeEnum.OPT_IN_ACTION_NODE:
      return PiBell;

    default:
      return LuPower;
  }
}

export function getNodeIconColorByType(type: NodeTypeEnum) {
  switch (type) {
    case NodeTypeEnum.START_NODE:
      return {
        iconColor: "text-green-500",
        borderColor: "border-primary-300",
        bgColor: "bg-primary/10",
      };
    case NodeTypeEnum.TRIGGER_NODE:
      return {
        iconColor: "text-green-500",
        border: "border-primary-300",
        bgColor: "bg-primary/10",
      };
    case NodeTypeEnum.COLLECT_NODE:
    case NodeTypeEnum.COLLECT_FORM_NODE:
      return {
        iconColor: "text-blue-500",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-300",
      };
    case NodeTypeEnum.BRANCH_NODE:
      return {
        iconColor: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-300",
      };
    case NodeTypeEnum.MESSAGE_NODE:
      return {
        iconColor: "text-blue-500",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-300",
      };
    case NodeTypeEnum.HTTP_ACTION_NODE:
    case NodeTypeEnum.EMAIL_ACTION_NODE:
      return {
        iconColor: "text-teal-600",
        bgColor: "bg-teal-50",
        borderColor: "border-teal-200",
      };
    case NodeTypeEnum.OPT_IN_ACTION_NODE:
      return {
        iconColor: "text-lavender-500",
        bgColor: "bg-lavender-50",
        borderColor: "border-lavender-300",
      };

    default:
      return {
        iconColor: "text-green-500",
        borderColor: "border-primary-300",
        bgColor: "bg-primary/10",
      };
  }
}

export const isPredefinedConfig = (
  config: PredefinedConditions | CustomConditions,
): config is PredefinedConditions => {
  return (config as PredefinedConditions).field !== undefined;
};

export const getMessageNodeIconByType = (type: MessageNodeFieldTypeEnum) => {
  switch (type) {
    case MessageNodeFieldTypeEnum.TEXT:
      return PiTextAlignLeftLight;
    case MessageNodeFieldTypeEnum.MEDIA:
      return CiImageOn;
    case MessageNodeFieldTypeEnum.QUICK_REPLY:
      return LuReply;
    case MessageNodeFieldTypeEnum.LINK_BUTTON:
      return PiLink;
  }
};

export const getMessageNodeDefaultValueByType = (
  type: MessageNodeFieldTypeEnum,
):
  | MessageNodeMediaSchema
  | MessageNodeQuickReplySchema
  | MessageNodeButtonSchema
  | MessageNodeTextSchema => {
  switch (type) {
    case MessageNodeFieldTypeEnum.TEXT:
      return {
        type: MessageNodeFieldTypeEnum.TEXT,
        message: "",
      };
    case MessageNodeFieldTypeEnum.MEDIA:
      return {
        type: MessageNodeFieldTypeEnum.MEDIA,
        fileType: "Media",
        customUrl: "",
      };
    case MessageNodeFieldTypeEnum.QUICK_REPLY:
      return {
        type: MessageNodeFieldTypeEnum.QUICK_REPLY,
        quickReplyLabel: "",
        quickReplyMessage: "",
      };
    case MessageNodeFieldTypeEnum.LINK_BUTTON:
      return {
        type: MessageNodeFieldTypeEnum.LINK_BUTTON,
        buttonLabel: "",
        buttonLink: "",
      };
  }
};

export function getNodeMeasurement({
  node,
  nodes,
}: {
  node?: Node;
  nodes: Node[];
}) {
  const currentWidth = node?.measured?.width || 0;
  const currentHeight = node?.measured?.height || 0;

  let currentX = node?.position.x ?? FLOW_DEFAULT_X;
  let currentY = node?.position.y ?? FLOW_DEFAULT_Y;

  // Change X position to my right + spacing
  currentX = currentX + currentWidth + FLOW_HORIZONTAL_SPACING;

  // For current Y,
  // Check find a suitable spot where this node will not overlap with other nodes
  // Keep trying positions until we find a clear spot
  let hasOverlap = true;
  let iterations = 0;
  while (hasOverlap) {
    hasOverlap = false;
    if (iterations > nodes.length) break;

    for (const existingNode of nodes) {
      if (existingNode.id === node?.id) continue;

      const nodeX = existingNode.position.x;
      const nodeY = existingNode.position.y;
      const nodeWidth = existingNode.measured?.width || 0;
      const nodeHeight = existingNode.measured?.height || 0;

      // Check if the new node would overlap horizontally
      const horizontalOverlap =
        currentX < nodeX + nodeWidth + FLOW_HORIZONTAL_SPACING &&
        currentX + currentWidth + FLOW_HORIZONTAL_SPACING > nodeX;

      // Check if the new node would overlap vertically
      const verticalOverlap =
        currentY < nodeY + nodeHeight + FLOW_VERTICAL_SPACING &&
        currentY + currentHeight + FLOW_VERTICAL_SPACING > nodeY;

      // If there's both horizontal and vertical overlap
      if (horizontalOverlap && verticalOverlap) {
        // Move the node down below the current obstacle
        currentY = nodeY + nodeHeight + FLOW_VERTICAL_SPACING;
        hasOverlap = true; // Keep checking until we find a clear spot
        break; // Restart checking with the new position
      }
    }
  }

  return {
    nodeWidth: currentWidth,
    nodeHeight: currentHeight,
    nextNodeXPosition: currentX,
    nextNodeYPosition: currentY,
  };
}

export function getNodeAccordionItemValue(args: {
  nodeType: NodeTypeEnum;
  fromCollectNode?: boolean;
  messageFieldIdx?: number;
  fieldIdx: number;
}) {
  switch (args.nodeType) {
    case NodeTypeEnum.MESSAGE_NODE:
      return `${args.nodeType}-field-${args.fieldIdx}${args.fromCollectNode ? `-message-${args.messageFieldIdx}` : ""}`;
    case NodeTypeEnum.COLLECT_FORM_NODE:
      return `${args.nodeType}-field-${args.fieldIdx}${args.fromCollectNode ? `-message-${args.messageFieldIdx}` : ""}`;
    case NodeTypeEnum.COLLECT_NODE:
      return `${args.nodeType}-field-${args.fieldIdx}`;
    default:
      return `${args.nodeType}-${args.fromCollectNode ? "collect" : ""}-${args.fieldIdx}`;
  }
}

export const isSourceHandleInEdges = ({
  edges,
  sourceHandle,
}: {
  edges: Edge[];
  sourceHandle: string;
}) => {
  return edges.some((edge) => edge.sourceHandle === sourceHandle);
};

export const stringContainsValidVariable = (
  value: string,
  validVariables: VariableInput[],
) => {
  const extractedVariables = getAllNamesBetweenMustaches(value);

  return extractedVariables.every((extractedVariable) => {
    return validVariables.some((validVariable) => {
      return constructVariableString(validVariable) === extractedVariable;
    });
  });
};
